<template>
    <kt-modal
        :visible="recordVisible"
        :title="recordTitle"
        @dismiss="onDismiss()"
        size="modal-lg"
    >
        <template #body>
            <assignment-form v-model="record"></assignment-form>
        </template>

        <template #footer>
            <div class="row w-100">
                <div :class="{ 'col-4': record.Id, 'col-6': !record.Id }">
                    <button @click="onDismiss()" class="btn btn-sm btn-outline-secondary form-control">Close</button>
                </div>
                <div class="col-4" v-if="record.Id">
                    <button @click="deleteRecord()" class="btn btn-sm btn-outline-danger form-control">Delete</button>
                </div>
                <div :class="{ 'col-4': record.Id, 'col-6': !record.Id }">
                    <button @click="saveRecord()" class="btn btn-sm btn-outline-primary form-control">Save</button>
                </div>
            </div>
        </template>
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import AssignmentForm from "@/components/Time/AssignmentForm.vue";
import KtModal from "@/components/UI/KtModal.vue";
import { TimeModel } from "@/models/TaskModel";

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Task Assignment");
const recordVisible = ref(true);

const props = defineProps({
    id: {
        type: [Number, String],
        default: null
    },
    requestId: {
        type: Number,
        default: 0
    },
    clientId: {
        type: Number,
        default: 0
    },
    workAssignment: {
        type: String,
        default: ''
    },
    isNew: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close']);

const unwatch: Array<Function> = [];

const newRecord = (): TimeModel => {
    const task = new TimeModel();
    task.Type = 'assignment';
    
    // Pre-populate with requestId if provided
    if (props.requestId) {
        task.RequestId = props.requestId;
    }
    
    // Pre-populate with clientId if provided
    if (props.clientId) {
        task.CustomerNumber = props.clientId;
    }
    
    // Pre-populate with workAssignment if provided
    if (props.workAssignment) {
        task.WorkAssignment = props.workAssignment;
    }
    
    return task;
};

const record = ref<TimeModel>(newRecord());

const saveRecord = async () => {
    // Ensure Type is set to assignment
    record.value.Type = 'assignment';
    
    let data = Helper.prep(record.value);
    console.debug("save assignment", data);
    
    data = await ApiService.saveTime(data);
    
    // Check if we're in a route-based context or global modal
    if (route.name && route.name.toString().includes('Assignment')) {
        Emitter.emit("redirect", "/time/edit/" + data.Id);
    } else {
        // Global modal - close after save
        Alert.notify({
            text: "Task assignment saved successfully!",
            type: "success"
        });
        emit('close');
    }
};

const deleteRecord = async () => {
    Alert.notify("Deletion is currently disabled.");
};

const onDismiss = () => {
    // Check if we're in a route-based context or global modal
    if (route.name && route.name.toString().includes('Assignment')) {
        router.back();
    } else {
        // Global modal - emit close event
        emit('close');
    }
};

const load = async () => {
    // Check prop first, then fall back to route params
    const id = props.id || route.params.id;

    if (id && id !== "new" && !props.isNew) {
        const data = await ApiService.loadTime(id as string);
        
        /*
        if (data.Type !== 'assignment') {
            Alert.notify("This task is not an assignment.");
            Emitter.emit("redirect", "/time/edit/" + id);
            return;
        }
        */
        
        recordTitle.value = "Edit Task Assignment";
        record.value = data;
    } else {
        recordTitle.value = "New Task Assignment";
        record.value = newRecord();
    }
};

// Watch for prop id changes
unwatch.push(watch(() => props.id, () => {
    load();
}, { immediate: true }));

// Watch for route changes (fallback for route-based usage)
unwatch.push(watch(() => route.params.id, () => {
    if (!props.id) {
        load();
    }
}, { immediate: true }));

onUnmounted(() => {
    unwatch.forEach(fn => fn());
});
</script>
